'use client'

/**
 * /register — Registrierungs-Einstieg
 *
 * 2026-05-08 (Phase 2 Tester-Flow): Wenn ?code=FGL-XXXX-YYYY in der URL ist,
 * wird der Code im Hintergrund validiert und das Register-Modal mit dem
 * Code im Tester-Modus geoeffnet. Ohne Code laeuft die Seite als
 * Marketing-Einstieg + Modal-Trigger fuer regulaere Anmeldung — die jedoch
 * waehrend der Tester-Phase serverseitig gesperrt ist (siehe register-Route).
 */

import { useEffect, useState, Suspense } from 'react'
import { useRouter, useSearchParams } from 'next/navigation'
import Link from 'next/link'
import { UserPlus, Upload, ShieldCheck, Award, Sparkles, Loader2 } from 'lucide-react'
import { FglHeader } from '@/components/layout/fgl-header'
import { LaunchCountdown } from '@/components/launch/launch-countdown'
import { useTranslation } from '@/i18n/client'
import {
  LenisProvider,
  Reveal,
  Magnetic,
  GliderBackdrop,
  GliderSwarm,
} from '@/components/award'

interface ValidationResult {
  ok: boolean
  testerName?: string
  testerFirst?: string
  language?: string
  reason?: string
}

function RegisterPageInner() {
  const { t } = useTranslation()
  const router = useRouter()
  const searchParams = useSearchParams()
  const code = searchParams.get('code')

  const [validating, setValidating] = useState(false)
  const [validation, setValidation] = useState<ValidationResult | null>(null)
  // Admin-Toggle: Wenn registration_enabled=false UND kein Tester-Code in URL,
  // wird die Seite als „nicht aktiv" angezeigt (Offline-Modus). Mit gueltigem
  // Tester-Code laeuft sie weiter, weil Tester-Phase die Sperre umgehen darf.
  const [registrationOnline, setRegistrationOnline] = useState<boolean | null>(null)
  // R101 (2026-05-31): Launch-Termin für Live-Countdown auf dieser Seite.
  const [launchAt, setLaunchAt] = useState<string | null>(null)

  useEffect(() => {
    fetch('/api/public/site-state')
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        setRegistrationOnline(d?.registrationEnabled === true)
        if (typeof d?.launchAt === 'string') setLaunchAt(d.launchAt)
      })
      .catch(() => setRegistrationOnline(false))
  }, [])

  // Bei vorhandenem ?code= → automatisch validieren
  useEffect(() => {
    if (!code) return
    setValidating(true)
    fetch('/api/auth/tester-code/validate', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code }),
    })
      .then((r) => r.json())
      .then((data) => setValidation(data))
      .catch(() => setValidation({ ok: false, reason: 'network_error' }))
      .finally(() => setValidating(false))
  }, [code])

  // Offline-Modus: Registration-Toggle aus + kein Tester-Code → Seite zeigt
  // nur einen Hinweis statt des Marketing-Trichters.
  const showOfflineMode = registrationOnline === false && !code

  // Bei valid Code → Modal oeffnet sich automatisch ueber FglHeader
  // (pathname === '/register' + ?code=... → setShowRegister(true) in fgl-header.tsx).
  // KEIN router.push noetig — vorher hatten wir push nach '/' was den User von der
  // Register-Seite zur Homepage geschickt hat und einen Hydration-Reload ausgeloest hat,
  // was als "Registrierung vorbei" wahrgenommen wurde.

  const benefits = [
    { icon: Upload, text: 'Unbegrenzte IGC-Uploads — sofort nach Registrierung' },
    { icon: Award, text: 'Auto-Upgrade auf Pro ab 25 validen Flügen, Premium ab 100' },
    { icon: ShieldCheck, text: 'DSGVO-konform · TOTP-2FA · On-Premise KI · keine US-Cloud' },
  ]

  // Code-Modus: spezielle Tester-Begrueßung
  const isTesterMode = !!code

  return (
    <LenisProvider>
      <div data-award="true" className="relative min-h-screen flex flex-col" style={{ background: 'var(--award-ink)' }}>
        <FglHeader />
        <GliderBackdrop />
        <GliderSwarm />

        <main className="relative flex-1 flex items-center justify-center overflow-hidden pt-[84px] pb-16">
          <div className="relative z-10 max-w-[1100px] mx-auto px-6 md:px-12">

            {/* Tester-Code-Status (nur wenn ?code= in URL) */}
            {isTesterMode && (
              <div className="mb-10 max-w-[700px] mx-auto">
                {validating && (
                  <div
                    className="p-5 rounded-[18px] flex items-center gap-3 justify-center"
                    style={{
                      background: 'var(--award-ink-2)',
                      border: '1px solid var(--award-line)',
                    }}
                  >
                    <Loader2 className="w-5 h-5 animate-spin" style={{ color: 'var(--award-gold)' }} />
                    <span className="text-[14px]" style={{ color: 'var(--award-cirrus-2)' }}>
                      Tester-Code <strong style={{ color: 'var(--award-gold)' }}>{code}</strong> wird geprüft …
                    </span>
                  </div>
                )}

                {!validating && validation && !validation.ok && (
                  <div
                    className="p-6 rounded-[18px]"
                    style={{
                      background: 'rgba(239,68,68,0.06)',
                      border: '1px solid rgba(239,68,68,0.4)',
                    }}
                  >
                    <div className="text-[14px] font-bold mb-2" style={{ color: '#ef4444' }}>
                      Tester-Code ungültig
                    </div>
                    <p className="text-[13px] mb-3" style={{ color: 'var(--award-cirrus-2)' }}>
                      {validation.reason === 'not_found' && 'Der angegebene Code existiert nicht. Prüfe noch einmal die Schreibweise — Format: FGL-XXXX-YYYY.'}
                      {validation.reason === 'already_claimed' && 'Dieser Code wurde bereits eingelöst. Wende dich an info@free-glide-league.com falls das ein Fehler ist.'}
                      {validation.reason === 'revoked' && 'Dieser Code wurde widerrufen. Bitte kontaktiere info@free-glide-league.com.'}
                      {validation.reason === 'invalid_format' && 'Falsches Code-Format. Erwartet wird FGL-XXXX-YYYY.'}
                      {(!validation.reason || validation.reason === 'server_error' || validation.reason === 'network_error') && 'Code-Prüfung fehlgeschlagen. Bitte später erneut versuchen oder info@free-glide-league.com kontaktieren.'}
                    </p>
                    <Link
                      href="/register"
                      className="inline-flex items-center gap-2 text-[12px] font-bold"
                      style={{ color: 'var(--award-gold)' }}
                    >
                      ← Zurück zur Registrierungs-Übersicht
                    </Link>
                  </div>
                )}

                {!validating && validation?.ok && (
                  <div
                    className="p-6 rounded-[18px] flex items-center gap-3"
                    style={{
                      background: 'color-mix(in oklch, var(--award-gold) 10%, transparent)',
                      border: '1.5px solid var(--award-gold)',
                      boxShadow: '0 0 24px var(--award-gold-glow)',
                    }}
                  >
                    <Sparkles className="w-5 h-5 shrink-0" style={{ color: 'var(--award-gold)' }} />
                    <div>
                      <div className="text-[14px] font-bold mb-1" style={{ color: 'var(--award-gold)' }}>
                        Willkommen{validation.testerFirst ? `, ${validation.testerFirst}` : ''}!
                      </div>
                      <p className="text-[13px]" style={{ color: 'var(--award-cirrus-2)' }}>
                        Dein FGL-Tester-Code <strong style={{ color: 'var(--award-gold)' }}>{code}</strong> ist gültig.
                        Das Registrierungs-Formular wird gleich geöffnet — als Dankeschön bekommst du <strong>Premium-Lifetime</strong>.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="text-center mb-10">
              <Reveal>
                <div className="flex items-center justify-center gap-3 mb-6 flex-wrap">
                  <span className="award-eyebrow">
                    {isTesterMode
                      ? 'Tester-Phase · Versteckte Registrierung'
                      : showOfflineMode
                        ? 'Registrierung aktuell nicht möglich'
                        : 'Registrieren · Steigen mit jedem Flug'}
                  </span>
                  <span className="h-px w-16 bg-[var(--award-line-hi)]" />
                  <span className="award-label">{isTesterMode ? 'Premium-Lifetime' : 'Public-Launch 03.06.2026 · 07:30 Berlin'}</span>
                </div>
              </Reveal>

              <Reveal delay={1}>
                <h1 className="award-h-mega">
                  {isTesterMode
                    ? <>Werde Tester der <span className="award-grad-text">FREE GLIDE LEAGUE.</span></>
                    : showOfflineMode
                      ? <span className="award-grad-text">Registrierung aktuell nicht möglich.</span>
                      : <>Werde <span className="award-grad-text">FREE GLIDE LEAGUE.</span></>}
                </h1>
              </Reveal>

              <Reveal delay={2}>
                <p className="award-body-lead mx-auto mt-6">
                  {isTesterMode
                    ? 'Du hast einen FGL-Tester-Code erhalten? Dann öffnet sich gleich das Registrierungs-Formular. Mit deiner Registrierung bekommst du als Dankeschön Premium-Lifetime — alle Features ohne Flugzahl-Limit.'
                    : showOfflineMode
                      ? 'Grund: Wir bereiten gerade den nächsten Schritt vor — Datenbank-Wartung, Engine-Update und letzter Sicherheits-Check vor dem öffentlichen Launch. Die offene Registrierung öffnet sich automatisch beim Ablauf des Countdowns.'
                      : t('auth.registerPageHint')}
                </p>
              </Reveal>

              {/* R101 (2026-05-31, Stephan-Wunsch): Live-Countdown bis Public-Launch.
                  Beim Ablauf schaltet das Backend registrationEnabled automatisch
                  auf true und die Seite reloaded — Stephan muss keinen Knopf drücken. */}
              {showOfflineMode && launchAt && (
                <Reveal delay={3}>
                  <div className="mt-8 flex justify-center">
                    <LaunchCountdown
                      launchAt={launchAt}
                      variant="hero"
                      onElapsed={() => {
                        setTimeout(() => window.location.reload(), 2500)
                      }}
                    />
                  </div>
                </Reveal>
              )}
            </div>

            {!isTesterMode && !showOfflineMode && (
              <>
                <div className="grid sm:grid-cols-3 gap-4 mb-10">
                  {benefits.map((b, i) => (
                    <Reveal key={i} delay={(i + 2) as 2 | 3 | 4}>
                      <div
                        className="p-5 rounded-[18px] h-full"
                        style={{
                          background: 'var(--award-ink-2)',
                          border: '1px solid var(--award-line)',
                        }}
                      >
                        <div
                          className="w-11 h-11 rounded-xl flex items-center justify-center mb-3"
                          style={{ background: 'var(--award-gold-soft)' }}
                        >
                          <b.icon className="w-5 h-5" style={{ color: 'var(--award-gold)' }} />
                        </div>
                        <p className="text-[13px] leading-relaxed" style={{ color: 'var(--award-cirrus-2)' }}>
                          {b.text}
                        </p>
                      </div>
                    </Reveal>
                  ))}
                </div>

                <Reveal delay={5}>
                  <div className="flex flex-col items-center gap-3">
                    {/* R101 (Stephan-Wunsch 2026-05-31): Echtnamen-Hinweis prominent
                        VOR der Registrierung — Piloten sollen schon vor dem Klick wissen
                        dass ihr Echtname für die Wertung gebraucht wird.
                        R102.7 (Stephan-Wunsch 2026-06-01): positiver formuliert. */}
                    <div
                      className="p-5 rounded-[18px] max-w-[700px] text-center"
                      style={{
                        background: 'rgba(242,201,78,0.10)',
                        border: '1px solid rgba(242,201,78,0.40)',
                      }}
                    >
                      <p className="text-[14px] font-black mb-2" style={{ color: 'var(--award-gold)' }}>
                        Vor- und Nachname sind Pflicht für die FGL-Wertung.
                      </p>
                      <p className="text-[13px]" style={{ color: 'var(--award-cirrus-2)' }}>
                        Ohne Echtname kein Eintrag in die Ranglisten der öffentlichen Darstellung, wie gewohnt.
                      </p>
                    </div>

                    <Magnetic>
                      <Link href="/login" className="award-btn award-btn-ghost">
                        Schon registriert? Zum Login
                      </Link>
                    </Magnetic>
                  </div>
                </Reveal>
              </>
            )}
          </div>
        </main>
      </div>
    </LenisProvider>
  )
}

export default function RegisterPage() {
  return (
    <Suspense fallback={null}>
      <RegisterPageInner />
    </Suspense>
  )
}
